import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { copyToClipboard } from 'utils/helpers/copyToClipboard';
import SolarIcon from 'components/icons/SolarIcon';

interface CopyTextWrapperProps {
    value: string;
    children: ReactNode;
    wrap?: boolean;
}

const CopyTextWrapper = ({ value, children, wrap = false }: CopyTextWrapperProps) => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const [copied, setCopied] = useState(false);
    const trimmedValue = String(value || '').trim();
    const canCopy = trimmedValue.length > 0;

    const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!canCopy) return;

        const button = e.currentTarget;
        const success = await copyToClipboard(trimmedValue);
        button.blur();

        if (!success) {
            showToast(t('messError'), 'danger');
            return;
        }

        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };

    return (
        <span className={`copy-text-wrapper${wrap ? ' copy-text-wrapper--wrap' : ''}`}>
            <span className="copy-text-wrapper-content">{children}</span>
            {canCopy && (
                <button
                    type="button"
                    className="copy-text-wrapper-btn"
                    title={t('clipboard.copy')}
                    aria-label={t('clipboard.copy')}
                    onClick={handleCopy}
                >
                    <SolarIcon name={copied ? 'check-lg' : 'copy'} className={copied ? 'text-success' : undefined} />
                </button>
            )}
        </span>
    );
};

export default CopyTextWrapper;
